import React, { useContext, useEffect, useState } from 'react';
import ConfirmDialogTemplate from '@/Components/Templates/ConfirmDialogTemplate';
import { fieldsPlaceholders } from '@/Config/forms';
import { Button } from 'primereact/button';
import TextInputTemplate from '@/Components/Templates/Forms/TextInputTemplate';
import { getFormErrorMessage, isFormFieldValid, validateForm } from '@/Helpers/forms';
import NumberInputTemplate from '@/Components/Templates/Forms/NumberInputTemplate';
import { apiRequest, toast } from '@/Helpers/general';
import { AppContext } from '@/Components/Context/AppContext';
import { choFields } from '@/Config/forms/cho';

/**
 * Component which renders a modal for creating a new CHO
 */
const AddChoModal = ({ visible, setVisible, choOptions, setChoOptions, claimFormData, setClaimFormData }) => {
    const { toastRef } = useContext(AppContext);

    const [formData, setFormData] = useState();
    const [formValidationRules, setFormValidationRules] = useState([]);
    const [formErrors, setFormErrors] = useState({});
    const [formLoading, setFormLoading] = useState(false);

    /**
     * Update the state of the form data (when changing an input value)
     *
     * @param {string} name the name of the input
     * @param {string} value the new value of the input
     */
    const changeFormData = (name, value) => {
        let getFormData = { ...formData };

        getFormData[name] = value;

        setFormData(getFormData);
    };

    /**
     * Generate input fields based on config
     *
     * @param {object} field object containing config data
     */
    const generateInputField = (field) => {
        switch (field.type) {
            case 'text':
                return (
                    <TextInputTemplate
                        name={field.name}
                        label={field.label}
                        placeholder={field.custom_placeholder || fieldsPlaceholders.text}
                        labelClassName="one-line"
                        value={formData ? formData[field.name] : ''}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                    />
                );

            case 'integer':
                return (
                    <NumberInputTemplate
                        name={field.name}
                        label={field.label}
                        placeholder={field.custom_placeholder || fieldsPlaceholders.integer}
                        labelClassName="one-line"
                        value={formData ? formData[field.name] : null}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                    />
                );

            default:
                break;
        }
    };

    const modalContent = (
        <div className="modal-wrap auto-height px-3">
            <div className="grid">
                {choFields.map((field) => {
                    return (
                        <div className="field col-12 md:col-6 lg:col-4" key={field.name}>
                            {generateInputField(field)}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    /**
     * Update the CHO dropdown and selected value
     *
     * @param {object} newCho object containing CHO data
     */
    const updateChoSelection = (newCho) => {
        if (newCho) {
            setChoOptions([{
                label: newCho.name,
                value: newCho.id
            }, ...choOptions]);

            let copyClaimFormData = {...claimFormData};

            copyClaimFormData['cho_id'] = newCho.id;

            setClaimFormData(copyClaimFormData);
        }
    };

    /**
     * Submits the create CHO form
     */
    const submitForm = async () => {
        setFormLoading(true);

        let validation = validateForm(formValidationRules, formData, setFormErrors);

        if (validation) {
            apiRequest('post', 'cho/', {
                data: formData,
                onSuccess: (response) => {
                    setFormLoading(false);

                    if (response?.data?.responseType == 'success') {
                        updateChoSelection(response.result);
                        setVisible(false);

                        toast(toastRef, 'success', 'The new CHO has been created.');

                    } else {
                        toast(toastRef, 'error', 'Error creating CHO.');
                    }
                },
                onFailure: (error) => {
                    setFormLoading(false);

                    if (error.status == 422) {
                        if (error.messages) {
                            let errorMessages = error.messages,
                                newFormErrors = {};

                            for (let key in errorMessages) {
                                if (Object.hasOwnProperty.call(errorMessages, key)) {
                                    newFormErrors[key] = errorMessages[key];
                                }
                            }

                            setFormErrors(newFormErrors);
                        } else {
                            toast(toastRef, 'error', 'Error creating CHO.');
                        }

                    } else {
                        toast(toastRef, 'error', 'Error creating CHO.');
                    }
                }
            });
        } else {
            setFormLoading(false);
        }
    };

    const widgetFooter = (
        <>
            <hr />

            <div className="text-right">
                <Button label="Save" onClick={submitForm} loading={formLoading} loadingIcon="pi pi-spin pi-sun" iconPos="center" />
            </div>
        </>
    );

    useEffect(() => {
        let initFormData = {},
            initFormValidationRules = [];

        choFields.forEach((field) => {
            initFormData[field.name] = field.defaultValue || '';

            if (field.validation && Object.keys(field.validation).length > 0) {
                initFormValidationRules.push({
                    field: field.name,
                    rules: field.validation,
                });
            }
        });

        setFormData(initFormData);
        setFormValidationRules(initFormValidationRules);
    }, []);

    return (
        <ConfirmDialogTemplate
            title={'Add a new CHO'}
            className="modal-tabs"
            message={modalContent}
            onConfirm={() => { }}
            visible={visible}
            setVisible={setVisible}
            onHide={() => setVisible(false)}
            footer={widgetFooter}
            noIcon
        />
    );
};

export default AddChoModal;
